import React from 'react';
import ReactDOM from 'react-dom';
import { useSubscription } from '../hooks/useSubscription';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/upgradePromptModal.css';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  limitType: 'items' | 'ai_scans' | 'storage';
  message?: string;
  onViewPlans?: () => void;
}

const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  isOpen,
  onClose,
  limitType,
  message,
  onViewPlans,
}) => {
  const { tierInfo, usage, formatLimit, formatStorage } = useSubscription();

  if (!isOpen || !tierInfo || !usage) return null;

  const limitMessages = {
    items: {
      title: 'Item Limit Reached',
      description: message || `You've reached your limit of ${formatLimit(usage.items.limit)} items.`,
      current: `${usage.items.used} / ${formatLimit(usage.items.limit)} items used`,
    },
    ai_scans: {
      title: 'AI Scan Limit Reached',
      description: message || `You've reached your monthly limit of ${formatLimit(usage.ai_scans_per_month.limit)} AI scans.`,
      current: `${usage.ai_scans_per_month.used} / ${formatLimit(usage.ai_scans_per_month.limit)} scans used this month`,
    },
    storage: {
      title: 'Storage Limit Reached',
      description: message || `You've reached your storage limit of ${formatStorage(usage.storage_mb.limit)}.`,
      current: `${formatStorage(usage.storage_mb.used)} / ${formatStorage(usage.storage_mb.limit)} used`,
    },
  };

  const info = limitMessages[limitType];

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="upgrade-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton onClick={onClose} />
        
        <h2>{info.title}</h2>
        <p className="upgrade-description">{info.description}</p>
        
        <div className="current-usage">
          <div className="usage-label">Current Usage</div>
          <div className="usage-value">{info.current}</div>
        </div>

        <div className="upgrade-benefits">
          <h3>Upgrade to get more:</h3>
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="benefit-icon">📦</span>
              <span className="benefit-text">More items</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">🤖</span>
              <span className="benefit-text">More AI scans</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">💾</span>
              <span className="benefit-text">More storage</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">👥</span>
              <span className="benefit-text">Group sharing</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">📄</span>
              <span className="benefit-text">Insurance docs</span>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">⚡</span>
              <span className="benefit-text">Priority support</span>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="upgrade-btn" onClick={() => {
            if (onViewPlans) {
              onViewPlans();
            }
            onClose();
          }}>
            View Plans
          </button>
          <button className="cancel-btn" onClick={onClose}>
            Maybe Later
          </button>
        </div>

        <div className="referral-tip">
          💡 Tip: Invite friends to earn bonus limits for free!
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UpgradePromptModal;
